import { cn } from "../../../utils/cn.js";
import { Spinner } from "../Spinner/Spinner.js";
import { cva } from "class-variance-authority";
import "react";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { Button as Button$1 } from "@base-ui/react/button";
//#region src/components/Button/Button.tsx
const buttonVariants = cva([
	"relative inline-flex items-center justify-center gap-xxs shrink-0 whitespace-nowrap",
	"rounded-xs font-body font-heading-medium tracking-base cursor-pointer",
	"border-(length:--border-width-default) border-solid",
	"transition-[color,background-color,border-color,box-shadow,scale,opacity] duration-150 ease-out",
	"active:scale-[0.98] active:opacity-80",
	"motion-reduce:active:scale-100",
	"[&_svg]:pointer-events-none [&_svg]:shrink-0",
	"focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-text-selected",
	"data-[disabled]:pointer-events-none data-[disabled]:cursor-not-allowed"
], {
	defaultVariants: {
		shape: "default",
		size: "small",
		variant: "brand"
	},
	variants: {
		shape: {
			default: "",
			icon: "px-0"
		},
		size: {
			small: "h-[28px] py-0 text-body-base leading-body-base [&_svg]:size-[12px]",
			base: "h-[32px] py-0 text-body-base leading-body-base [&_svg]:size-[12px]",
			large: "h-[36px] py-0 text-body-medium leading-body-medium [&_svg]:size-[14px]"
		},
		variant: {
			"brand": [
				"text-text-brand-button border-transparent",
				"bg-background-brand-primary",
				"hover:bg-background-brand-primary-hover",
				"active:bg-background-brand-primary-pressed",
				"[&_svg]:text-icon-brand-button",
				"data-[disabled]:bg-background-disabled data-[disabled]:border-border-disabled data-[disabled]:text-text-disabled data-[disabled]:[&_svg]:text-icon-disabled"
			],
			"danger": [
				"text-text-danger border-transparent",
				"bg-background-system-danger",
				"hover:bg-background-system-danger-hover",
				"active:bg-background-system-danger-pressed",
				"[&_svg]:text-icon-danger",
				"data-[disabled]:bg-background-disabled data-[disabled]:border-border-disabled data-[disabled]:text-text-disabled data-[disabled]:[&_svg]:text-icon-disabled"
			],
			"danger-bold": [
				"text-text-primary-inverse border-transparent",
				"bg-background-system-danger-bold",
				"hover:bg-background-system-danger-bold-hover",
				"active:bg-background-system-danger-bold-pressed",
				"[&_svg]:text-icon-inverse",
				"data-[disabled]:bg-background-disabled data-[disabled]:border-border-disabled data-[disabled]:text-text-disabled data-[disabled]:[&_svg]:text-icon-disabled"
			],
			"primary": [
				"text-text-primary border-border-primary shadow-elevation-element",
				"bg-background-neutral-primary",
				"hover:bg-background-neutral-primary-hover",
				"active:bg-background-neutral-primary-pressed",
				"[&_svg]:text-icon-primary",
				"data-[disabled]:bg-background-disabled data-[disabled]:border-border-disabled data-[disabled]:text-text-disabled data-[disabled]:shadow-none data-[disabled]:[&_svg]:text-icon-disabled"
			],
			"secondary": [
				"text-text-primary border-border-primary",
				"bg-background-neutral-secondary",
				"hover:bg-background-neutral-secondary-hover",
				"active:bg-background-neutral-secondary-pressed",
				"[&_svg]:text-icon-primary",
				"data-[disabled]:bg-background-disabled data-[disabled]:border-border-disabled data-[disabled]:text-text-disabled data-[disabled]:[&_svg]:text-icon-disabled"
			],
			"tertiary": [
				"text-text-primary border-transparent",
				"bg-background-neutral-secondary",
				"hover:bg-background-neutral-secondary-hover",
				"active:bg-background-neutral-secondary-pressed",
				"[&_svg]:text-icon-primary",
				"data-[disabled]:bg-transparent data-[disabled]:text-text-disabled data-[disabled]:[&_svg]:text-icon-disabled"
			]
		}
	},
	compoundVariants: [
		{
			shape: "icon",
			size: "small",
			className: "w-[28px]"
		},
		{
			shape: "icon",
			size: "base",
			className: "w-[32px]"
		},
		{
			shape: "icon",
			size: "large",
			className: "w-[36px]"
		},
		{
			shape: "default",
			size: "small",
			className: "px-[10px]"
		},
		{
			shape: "default",
			size: "base",
			className: "px-sm"
		},
		{
			shape: "default",
			size: "large",
			className: "px-[14px]"
		}
	]
});
const spinnerSizeMap = {
	small: "xs",
	base: "sm",
	large: "sm"
};
/**
* Curri-styled button built on the Base UI Button primitive.
*
* A polymorphic button that accepts arbitrary children — text, icons, or both —
* and auto-sizes descendant SVGs via `[&_svg]` selectors based on the `size`
* prop. Use `shape="icon"` for icon-only buttons to apply square padding.
* Disabled styling is applied automatically via the `data-[disabled]` attribute.
*
* When `loading` is true the button is disabled and its content is replaced by
* a spinner. The original content is rendered invisibly to preserve the
* button's intrinsic width during the loading state.
*
* @param props - Button configuration and Base UI props
* @returns The rendered button element
*/
function Button({ children, className, disabled, label, loading = false, shape = "default", size = "small", variant = "brand", ...props }) {
	const content = children || label;
	return /* @__PURE__ */ jsx(Button$1, {
		className: cn(buttonVariants({
			shape,
			size,
			variant
		}), className),
		disabled: disabled || loading,
		...props,
		children: loading ? /* @__PURE__ */ jsxs(Fragment, { children: [/* @__PURE__ */ jsx("span", {
			className: "invisible contents",
			children: content
		}), /* @__PURE__ */ jsx("span", {
			className: "absolute inset-0 flex items-center justify-center",
			children: /* @__PURE__ */ jsx(Spinner, { size: spinnerSizeMap[size] })
		})] }) : content
	});
}
//#endregion
export { Button, buttonVariants };

//# sourceMappingURL=Button.js.map